<template>
  <div>
    <FlashMessage v-if="messages.text" :message="messages" />

    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      @click.self="$emit('close')"
    >
      <div
        class="flex w-full max-w-2xl flex-col rounded-xl bg-white shadow-xl"
        style="max-height: 90vh"
      >
        <!-- Header -->
        <div class="flex items-center justify-between border-b px-8 py-5">
          <div>
            <h2 class="text-xl font-semibold text-gray-800">Paste File</h2>
            <div class="mt-2 flex items-center gap-2">
              <svg
                class="h-4 w-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-width="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <p class="max-w-sm truncate text-sm text-gray-500">
                {{ file?.name }}
              </p>
            </div>
          </div>
          <button
            @click="$emit('close')"
            class="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        <!-- Breadcrumbs -->
        <div
          class="flex flex-wrap items-center gap-1 border-b bg-gray-50 px-8 py-3"
        >
          <!-- <button
            @click="loadRoot"
            :class="
              breadcrumbs.length === 0
                ? 'cursor-default text-gray-400'
                : 'text-blue-600 hover:text-blue-800'
            "
            class="text-sm font-medium"
          >
            Home
          </button> -->

          <template v-for="(crumb, idx) in breadcrumbs" :key="crumb.id">
            <span class="mx-1 text-gray-400" v-if="idx !== 0">›</span>

            <button
              v-if="idx < breadcrumbs.length - 1"
              @click="loadFolder(crumb.id)"
              class="max-w-[160px] truncate text-sm text-blue-600 hover:text-blue-800"
            >
              {{ crumb.name }}
            </button>

            <span
              v-else
              class="max-w-[160px] truncate text-sm font-medium text-gray-600"
            >
              {{ crumb.name }}
            </span>
          </template>
        </div>
        <div class="flex-1 overflow-y-auto">
          <div v-if="loading" class="py-24 text-center text-gray-400">
            Loading folders…
          </div>

          <div
            v-else-if="folderDetails.folders.length === 0"
            class="flex flex-col items-center justify-center py-24 text-gray-400"
          >
            <svg
              class="h-16 w-16 mb-4 text-gray-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-width="1.5"
                d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
              />
            </svg>
            <p class="text-sm font-medium">No subfolders here</p>
          </div>
          <ul v-else class="divide-y divide-gray-100">
            <li
              v-for="folder in folderDetails.folders"
              :key="folder.id"
              class="group relative"
              :class="selectedFolderId === folder.id ? 'bg-blue-50' : ''"
            >
              <div
                v-if="selectedFolderId === folder.id"
                class="absolute left-0 top-0 bottom-0 w-1 bg-blue-600 rounded-r"
              ></div>
              <div
                class="flex items-center justify-between px-8 py-4 hover:bg-gray-50 cursor-pointer transition-colors"
                @click="selectFolder(folder)"
              >
                <!-- Folder info with icon -->
                <div class="flex min-w-0 flex-1 items-center gap-3">
                  <svg
                    class="h-5 w-5 flex-shrink-0"
                    :class="
                      selectedFolderId === folder.id
                        ? 'text-blue-600'
                        : 'text-yellow-500'
                    "
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-width="1.8"
                      d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                    />
                  </svg>

                  <div class="min-w-0 flex-1">
                    <span
                      class="block truncate text-sm font-medium"
                      :class="
                        selectedFolderId === folder.id
                          ? 'text-blue-700'
                          : 'text-gray-700'
                      "
                      @dblclick.stop="
                        resetSelection();
                        loadFolder(folder.id);
                      "
                    >
                      {{ folder.name }}
                    </span>
                  </div>
                </div>

                <div
                  class="flex items-center gap-2 opacity-0 transition group-hover:opacity-100"
                >
                  <!-- <button
                    @click.stop="moveDirectly(folder)"
                    class="rounded-md bg-blue-600 px-3 py-1 text-xs text-white hover:bg-blue-700"
                  >
                    Past
                  </button> -->

                  <button
                    @click.stop="loadFolder(folder.id)"
                    class="flex h-8 w-8 items-center justify-center rounded-full text-lg text-gray-500 hover:bg-blue-50 hover:text-blue-600"
                    title="Open folder"
                  >
                    ›
                  </button>
                </div>
              </div>
            </li>
          </ul>
        </div>

        <!-- Footer info -->
        <div class="border-t border-gray-100 bg-gray-50/50 px-8 py-4">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2 text-sm text-gray-500">
              <svg
                class="h-4 w-4"
                :class="selectedFolderId ? 'text-blue-600' : 'text-gray-400'"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-width="2"
                  d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                />
              </svg>
              <span v-if="selectedFolderId">
                Paste to:
                <strong class="text-gray-700 ml-1">{{
                  selectedFolderName
                }}</strong>
              </span>
              <span v-else class="italic">Select a destination folder</span>
            </div>

            <span class="text-xs text-gray-400">
              {{ folderDetails.folders.length }} folder{{
                folderDetails.folders.length !== 1 ? "s" : ""
              }}
            </span>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex justify-end gap-3 border-t px-8 py-5">
          <button
            @click="$emit('close')"
            class="rounded-lg bg-gray-100 px-5 py-2 text-sm"
          >
            Cancel
          </button>

          <button
            @click="confirmPaste"
            class="rounded-lg px-6 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            Paste Here
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { mapGetters } from "vuex";
import FlashMessage from "@/components/FlashMessage.vue";

export default {
  name: "CopyFilePopup",
  components: { FlashMessage },

  props: {
    file: { type: Object, required: true },
    startFolderId: { type: [Number, String], default: null },
    token: { type: [Number, String], default: null },
  },

  emits: ["close", "moved", "move-error"],

  data() {
    return {
      folderDetails: { folders: [] },
      breadcrumbs: [],
      currentFolderId: null,
      selectedFolderId: null,
      selectedFolderName: "",
      loading: false,
      messages: { text: "", type: null },
    };
  },

  computed: {
    ...mapGetters(["userInfo"]),
  },

  mounted() {
    this.startFolderId ? this.loadFolder(this.startFolderId) : this.loadRoot();
  },

  methods: {
    // async moveDirectly(folder) {
    //   this.selectedFolderId = folder.id;
    //   this.selectedFolderName = folder.name;

    //   await this.confirmPaste();
    // },
    async getFolderDetails(folderId = null) {
      this.loading = true;

      try {
         const token = this.token;
        const url = folderId
          ? `${process.env.VUE_APP_API}/folder/file/list/?Key=${token}&folder_id=${folderId}`
          : `${process.env.VUE_APP_API}/folder/file/list/?Key=${token}`;

        const res = await axios.get(url);
        const data = res.data;

        this.folderDetails = {
          folders: data.folders || [],
        };

        this.breadcrumbs = (data.folder_path || []).map((item) => ({
          id: item.id,
          name: item.name,
        }));

        this.currentFolderId = folderId ?? null;
        if (this.currentFolderId && this.folderDetails.folders.length === 0) {
          this.selectedFolderId = this.currentFolderId;
          this.selectedFolderName = this.breadcrumbs.at(-1)?.name || "Home";
        } else {
          this.resetSelection();
        }
      } catch (e) {
        this.messages = {
          text: "Failed to load folders",
          type: "error",
        };
      } finally {
        this.loading = false;
      }
    },
    loadRoot() {
      this.resetSelection();
      this.getFolderDetails(null);
    },

    loadFolder(folderId) {
      this.resetSelection();
      this.getFolderDetails(folderId);
    },

    resetSelection() {
      this.selectedFolderId = null;
      this.selectedFolderName = "";
    },

    selectFolder(folder) {
      if (this.selectedFolderId === folder.id) {
        this.resetSelection();
      } else {
        this.selectedFolderId = folder.id;
        this.selectedFolderName = folder.name;
      }
    },

    selectCurrentFolder() {
      if (!this.currentFolderId) return;
      this.selectedFolderId = this.currentFolderId;
      this.selectedFolderName = this.breadcrumbs.at(-1)?.name || "Home";
    },

    async confirmPaste() {
      if (!this.currentFolderId && !this.selectedFolderId) return;

  const targetFolderId = this.selectedFolderId || this.currentFolderId;
      try {
        const res = await axios.post(
          `${process.env.VUE_APP_API}/drive-files/${this.file.id}/copy/?Key=${this.userInfo.token_key}`,
          { target_folder_id: targetFolderId,edited_user:this.userInfo.user.id  },
        );

        this.$emit("paste", { message: res.data.message });
        this.$emit("close");
      } catch (e) {
        this.messages = {
          text: "Failed to copy file",
          type: "error",
        };
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
